import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { DepthAOPass } from './ambient-occlusion';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import type { LightingProfile } from './lighting-profile';
import type { GraphicsCapabilities, effectiveGraphics } from './graphics-settings';

export function graphicsCapabilities(renderer: THREE.WebGLRenderer): GraphicsCapabilities {
  const gl = renderer.getContext() as WebGL2RenderingContext, hdr = renderer.extensions.has('EXT_color_buffer_float') || renderer.extensions.has('EXT_color_buffer_half_float');
  const samples = gl.getInternalformatParameter(gl.RENDERBUFFER, hdr ? gl.RGBA16F : gl.RGBA8, gl.SAMPLES) as Int32Array;
  return { hdr, maxSamples: Math.max(0, ...samples), maxAnisotropy: Math.max(1, renderer.capabilities.getMaxAnisotropy()), maxTextureSize: renderer.capabilities.maxTextureSize };
}

/** Owns all post-processing targets. Replacing a quality mode releases the old GPU resources. */
export class GraphicsPipeline {
  private composer: EffectComposer;
  private fxaa: ShaderPass | null = null;
  private ao: DepthAOPass | null = null;
  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera, quality: ReturnType<typeof effectiveGraphics>, hdr: boolean, bloom: LightingProfile['bloom']) {
    const target = new THREE.WebGLRenderTarget(1, 1, { type: hdr ? THREE.HalfFloatType : THREE.UnsignedByteType, samples: quality.samples });
    if (quality.ambientOcclusion !== 'off') target.depthTexture = new THREE.DepthTexture(1, 1, THREE.UnsignedIntType);
    this.composer = new EffectComposer(renderer, target); this.composer.setPixelRatio(1);
    this.composer.addPass(new RenderPass(scene, camera));
    if (quality.ambientOcclusion !== 'off') {
      this.ao = new DepthAOPass(scene, camera, quality.ambientOcclusion === 'high' ? .5 : .25);
      this.ao.blendIntensity=quality.aoStrength;
      this.composer.addPass(this.ao);
    }
    if (quality.bloom && bloom.strength > 0) this.composer.addPass(new UnrealBloomPass(new THREE.Vector2(1,1),bloom.strength,bloom.radius,bloom.threshold));
    this.composer.addPass(new OutputPass());
    if (quality.antialiasing === 'fxaa') { this.fxaa = new ShaderPass(FXAAShader); this.composer.addPass(this.fxaa); }
  }
  resize(width: number, height: number): void {
    this.composer.setSize(width, height);
    this.fxaa?.uniforms.resolution!.value.set(1 / width, 1 / height);
  }
  render(dt: number): void { this.composer.render(dt); }
  setAOStrength(strength: number): void { if(this.ao) this.ao.blendIntensity=strength; }
  get aoResolution(): [number, number] | null { return this.ao ? [this.ao.width, this.ao.height] : null; }
  dispose(): void { for (const pass of this.composer.passes) pass.dispose(); this.composer.dispose(); }
}
